import React from 'react';
import { Check, Pencil } from 'lucide-react';
import {
  orderLineQty,
  type OpenOwnOrder,
  type PosOrderGroup,
} from '../../utils/pickListGrouping';
import type { LongPressRowHandlers } from '../../hooks/useLongPressSelect';

interface OwnDeliveryStoreCardProps {
  group: PosOrderGroup;
  selecting: boolean;
  selected: Set<string>;
  bindRow: (id: string, enabled: boolean) => LongPressRowHandlers;
  onEdit?: () => void;
}

export const OwnDeliveryStoreCard: React.FC<OwnDeliveryStoreCardProps> = ({
  group,
  selecting,
  selected,
  bindRow,
  onEdit,
}) => {
  const meta = [group.posCity, `${group.lineCount} line${group.lineCount === 1 ? '' : 's'}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <header className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-gray-900 leading-5 line-clamp-2">
            {group.posName}
          </p>
          <p className="text-xs text-gray-500 truncate leading-4 mt-0.5">{meta}</p>
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="flex-shrink-0 h-11 px-3.5 rounded-xl border border-gray-200 bg-gray-100 text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5"
          >
            <Pencil size={14} strokeWidth={2.25} />
            Edit
          </button>
        ) : null}
      </header>
      <ul>
        {group.orders.map((order) => (
          <LineRow
            key={order.id}
            order={order}
            selecting={selecting}
            selected={selected.has(order.id)}
            handlers={bindRow(order.id, !order.picked)}
          />
        ))}
      </ul>
    </article>
  );
};

function LineRow({
  order,
  selecting,
  selected,
  handlers,
}: {
  order: OpenOwnOrder;
  selecting: boolean;
  selected: boolean;
  handlers: LongPressRowHandlers;
}) {
  return (
    <li className="border-t border-gray-100">
      <button
        type="button"
        className={`w-full min-h-[48px] px-4 py-2.5 flex items-start gap-3 text-left ${
          selected ? 'bg-red-50' : ''
        }`}
        {...handlers}
      >
        {selecting && !order.picked ? (
          <span
            className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              selected ? 'bg-red-600 border-red-600 text-white' : 'border-gray-300'
            }`}
          >
            {selected ? <Check size={12} /> : null}
          </span>
        ) : null}
        <span className="flex-shrink-0 pt-0.5 text-right text-sm font-semibold tabular-nums text-gray-900">
          {orderLineQty(order)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-2">
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-gray-900 leading-5 line-clamp-2">
                {order.product_name}
              </span>
              {order.variant_name ? (
                <span className="block text-xs text-gray-400 truncate leading-4 mt-0.5">
                  {order.variant_name}
                </span>
              ) : null}
            </span>
            {order.picked ? (
              <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                Picked
              </span>
            ) : null}
          </span>
        </span>
      </button>
    </li>
  );
}
